import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, Filter, Users, Heart, MapPin, Phone } from 'lucide-react';
import api from '../utils/api';

const BloodDonorRegistry = () => {
  const { user, isAuthenticated } = useAuth();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBloodGroup, setSelectedBloodGroup] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  useEffect(() => {
    if (isAuthenticated) {
      fetchDonors();
    }
  }, [isAuthenticated]);

  const fetchDonors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/blood-donors');
      setDonors(response.data);
    } catch (error) {
      console.error('Error fetching donors:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDonors = donors.filter(donor => {
    const matchesSearch = donor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         donor.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBloodGroup = !selectedBloodGroup || donor.bloodGroup === selectedBloodGroup;
    const matchesCity = !selectedCity || donor.city?.toLowerCase().includes(selectedCity.toLowerCase());
    
    return matchesSearch && matchesBloodGroup && matchesCity;
  });

  const getBloodGroupColor = (bloodGroup) => {
    const colors = {
      'A+': 'text-red-600',
      'A-': 'text-red-500',
      'B+': 'text-blue-600',
      'B-': 'text-blue-500',
      'AB+': 'text-purple-600',
      'AB-': 'text-purple-500',
      'O+': 'text-green-600',
      'O-': 'text-green-500'
    };
    return colors[bloodGroup] || 'text-gray-600';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src="/aim-logo.png" 
                alt="AIM Foundation Logo" 
                className="w-20 h-20 md:w-24 md:h-24 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Blood Donor Registry
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Access our comprehensive database of registered blood donors
            </p>
          </div>
        </section>

        {/* Authentication Required */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <Users className="w-16 h-16 text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Registration Required
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              To access the Blood Donor Registry and view available donors, you must be registered and logged in to your AIM Foundation account.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/login"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                Login to Your Account
              </a>
              <a
                href="/register"
                className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                Create New Account
              </a>
            </div>
            <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Why Registration is Required
              </h3>
              <ul className="text-left space-y-2 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Protect donor privacy and personal information</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Ensure responsible access to donor database</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Track usage and maintain data security</span>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Comply with POPIA data protection requirements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/aim-logo.png" 
              alt="AIM Foundation Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Blood Donor Registry
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Find available blood donors in your area
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Blood Group Filter */}
            <select
              value={selectedBloodGroup}
              onChange={(e) => setSelectedBloodGroup(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Blood Groups</option>
              {bloodGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>

            {/* City Filter */}
            <input
              type="text"
              placeholder="Filter by city..."
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Available Donors ({filteredDonors.length})
            </h2>
            {loading && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading donors...</p>
            </div>
          ) : filteredDonors.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">
                {donors.length === 0 ? 'No donors registered yet.' : 'No donors match your search criteria.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDonors.map((donor) => (
                <div key={donor._id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {donor.name}
                    </h3>
                    <span className={`text-lg font-bold ${getBloodGroupColor(donor.bloodGroup)}`}>
                      {donor.bloodGroup}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-600 dark:text-gray-300">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{donor.city}, {donor.state}</span>
                    </div>
                    
                    {donor.phone && (
                      <div className="flex items-center text-gray-600 dark:text-gray-300">
                        <Phone className="w-4 h-4 mr-2" />
                        <span>{donor.phone}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center mt-4">
                      <div className={`w-3 h-3 rounded-full mr-2 ${donor.isAvailable ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={`text-sm ${donor.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                        {donor.isAvailable ? 'Available' : 'Currently Unavailable'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Information Section */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Important Information
          </h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <li>• All donors have consented to be listed in this registry</li>
            <li>• Contact donors directly to arrange blood donation</li>
            <li>• Always verify blood type compatibility before donation</li>
            <li>• Respect donor availability and preferences</li>
            <li>• Report any issues or concerns to AIM Foundation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BloodDonorRegistry;
